import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Navigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import {
  Shield,
  Users,
  UserPlus,
  UserCheck,
  UserX,
  Search,
  Filter,
  Mail,
  Edit,
  Save,
  X,
  Check,
  ChevronDown,
  AlertCircle,
  RefreshCw,
  Trash2,
  Calendar,
  FileText,
} from "lucide-react";

const AdminPanel = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mainSection, setMainSection] = useState("users"); // "users" | "reports"
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [emailModalUser, setEmailModalUser] = useState(null);
  const [calendarModalUser, setCalendarModalUser] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [addToAllCalendarOpen, setAddToAllCalendarOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", onConfirm: null });

  const modules = {
    directions: [
      { id: "general", name: "Dirección General" },
      { id: "operaciones", name: "Dirección de Operaciones y Logística" },
      { id: "talento", name: "Dirección de Talento y Bienestar" },
      { id: "academia", name: "Dirección Académica y de Innovación Educativa" },
      { id: "comunicacion", name: "Dirección de Comunicación y Experiencia" },
      { id: "finanzas", name: "Dirección de Estrategia y Finanzas" },
      { id: "informacion", name: "Dirección de Investigación e Información" },
      { id: "tecnologia", name: "Dirección de Desarrollo Tecnológico e Innovación" },
    ],
    divisions: [
      { id: "learn", name: "WCA | Learn" },
      { id: "network", name: "WCA | Network" },
      { id: "labs", name: "WCA | Labs" },
      { id: "impact", name: "WCA | Impact" },
    ],
  };

  const roles = [
    { id: "admin", name: "Administrador", description: "Acceso total" },
    { id: "director", name: "Director", description: "Acceso asignado" },
    { id: "member", name: "Miembro", description: "Solo lectura" },
    { id: "pending", name: "Pendiente", description: "Sin acceso" },
  ];

  // Check if user is super admin
  const isSuperAdmin = user?.is_super_admin || 
    user?.email === "contacto@ecosistemawca.com" || 
    user?.email === "ecosistemawca@gmail.com";

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [allUsersRes, pendingRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/users/pending`, { withCredentials: true }),
      ]);
      
      const allUsers = allUsersRes.data || [];
      const pending = pendingRes.data || [];
      
      console.log("Fetched users:", allUsers.length);
      console.log("Fetched pending users:", pending.length);
      console.log("Pending users data:", pending);
      
      setUsers(allUsers);
      setPendingUsers(pending);
    } catch (error) {
      console.error("Error fetching users:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      // Show error message to user
      if (error.response?.status === 403) {
        alert("No tienes permisos para acceder a esta sección");
      } else if (error.response?.status === 401) {
        alert("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
      } else {
        console.error("Error details:", error.response?.data || error.message);
      }
      // Set empty arrays on error
      setUsers([]);
      setPendingUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin && user) {
      fetchUsers();
    }
  }, [isSuperAdmin, user, fetchUsers]);

  const fetchReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      const res = await axios.get(`${API}/reports`, { withCredentials: true });
      setReports(res.data || []);
    } catch (e) {
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin && mainSection === "reports") {
      fetchReports();
    }
  }, [isSuperAdmin, mainSection, fetchReports]);

  const fetchCalendarEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calendar/events`, { withCredentials: true });
      setCalendarEvents(res.data || []);
    } catch (e) {
      setCalendarEvents([]);
    }
  }, []);

  const handleReportStatus = async (reportId, status) => {
    try {
      await axios.patch(`${API}/reports/${reportId}`, { status }, { withCredentials: true });
      fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar estado");
    }
  };

  const handleDeleteReport = (reportId) => {
    setConfirmModal({
      open: true,
      title: "Eliminar reporte",
      message: "¿Eliminar este reporte?",
      onConfirm: async () => {
        await axios.delete(`${API}/reports/${reportId}`, { withCredentials: true });
        toast.success("Reporte eliminado");
        fetchReports();
      },
    });
  };

  const handleAddEventToUserCalendar = async (userId, eventId) => {
    try {
      await axios.post(
        `${API}/admin/calendar/events/${eventId}/add-to-users`,
        { user_ids: [userId] },
        { withCredentials: true }
      );
      setCalendarModalUser(null);
    } catch (e) {
      alert(e.response?.data?.detail || "Error al añadir al calendario");
    }
  };

  const handleAddEventToAllCalendars = async (eventId) => {
    try {
      await axios.post(
        `${API}/admin/calendar/events/${eventId}/add-to-users`,
        { user_ids: [] },
        { withCredentials: true }
      );
      setAddToAllCalendarOpen(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Error al añadir a los calendarios");
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/activate`, {}, { withCredentials: true });
      fetchUsers();
    } catch (error) {
      console.error("Error activating user:", error);
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/deactivate`, {}, { withCredentials: true });
      fetchUsers();
    } catch (error) {
      console.error("Error deactivating user:", error);
    }
  };

  const handleDeleteUser = (userId, userName) => {
    setConfirmModal({
      open: true,
      title: "Eliminar usuario",
      message: `¿Estás seguro de que deseas eliminar al usuario "${userName}"? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
          toast.success("Usuario eliminado");
          fetchUsers();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Error al eliminar usuario");
        }
      },
    });
  };

  const handleUpdatePermissions = async (userId, permissions) => {
    try {
      await axios.put(`${API}/users/${userId}/permissions`, permissions, {
        withCredentials: true,
      });
      fetchUsers();
      setSelectedUser(null);
    } catch (error) {
      console.error("Error updating permissions:", error);
    }
  };

  // Redirect non-admin users
  if (!isSuperAdmin) {
    return <Navigate to="/general" replace />;
  }

  const filteredUsers = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayUsers = activeTab === "pending" ? pendingUsers : filteredUsers;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
            <Shield size={24} className="text-white sm:w-7 sm:h-7" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Panel de Administración</h1>
            <p className="text-sm sm:text-base text-slate-500">Gestión de usuarios y permisos</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {mainSection === "users" && (
            <>
              <button
                onClick={() => { setAddToAllCalendarOpen(true); fetchCalendarEvents(); }}
                className="btn-secondary flex items-center gap-2 flex-1 sm:flex-none justify-center"
              >
                <Calendar size={18} />
                Añadir evento a calendarios
              </button>
              <button
                onClick={fetchUsers}
                className="btn-secondary flex items-center gap-2 flex-1 sm:flex-none justify-center"
                data-testid="refresh-users-btn"
              >
                <RefreshCw size={18} />
                Actualizar
              </button>
            </>
          )}
          {mainSection === "reports" && (
            <button onClick={fetchReports} className="btn-secondary flex items-center gap-2">
              <RefreshCw size={18} />
              Actualizar
            </button>
          )}
        </div>
      </div>

      {/* Section tabs: Usuarios | Reportes */}
      <div className="glass-panel p-1 inline-flex rounded-full">
        <button
          onClick={() => setMainSection("users")}
          className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all text-sm sm:text-base flex items-center gap-2 ${
            mainSection === "users" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          <Users size={18} />
          Usuarios
        </button>
        <button
          onClick={() => setMainSection("reports")}
          className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all text-sm sm:text-base flex items-center gap-2 ${
            mainSection === "reports" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          <FileText size={18} />
          Reportes ({reports.length})
        </button>
      </div>

      {mainSection === "reports" && (
        <>
          {loadingReports ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="glass-panel overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700">Tipo</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Contenido</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Estado</th>
                      <th className="text-left p-4 font-semibold text-slate-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reports.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50/50">
                        <td className="p-4">
                          <span className="badge bg-slate-100 text-slate-700">{r.type || "—"}</span>
                        </td>
                        <td className="p-4 text-sm text-slate-700 max-w-xs truncate" title={r.content}>{r.content || "—"}</td>
                        <td className="p-4">
                          <span className={`badge ${
                            (r.status || "pendiente") === "aprobado" ? "badge-success" :
                            (r.status || "pendiente") === "en_revision" ? "badge-primary" :
                            (r.status || "pendiente") === "cancelado" ? "bg-slate-200 text-slate-600" :
                            "badge-warning"
                          }`}>
                            {(r.status || "pendiente").replace("_", " ")}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              value={r.status || "pendiente"}
                              onChange={(e) => handleReportStatus(r.id, e.target.value)}
                              className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
                            >
                              <option value="pendiente">Pendiente</option>
                              <option value="en_revision">En revisión</option>
                              <option value="aprobado">Aprobado</option>
                              <option value="cancelado">Cancelado</option>
                            </select>
                            <button
                              onClick={() => handleDeleteReport(r.id)}
                              className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg"
                              title="Eliminar"
                              data-testid={`delete-report-${r.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {reports.length === 0 && (
                <div className="p-12 text-center text-slate-500">
                  <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                  No hay reportes
                </div>
              )}
            </div>
          )}
        </>
      )}

      {mainSection === "users" && (
        <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass-panel p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Users size={24} className="text-primary" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">{users.length}</p>
            <p className="text-sm text-slate-500">Total Usuarios</p>
          </div>
        </div>
        <div className="glass-panel p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
            <UserCheck size={24} className="text-emerald-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">
              {users.filter((u) => u.is_active).length}
            </p>
            <p className="text-sm text-slate-500">Activos</p>
          </div>
        </div>
        <div className="glass-panel p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
            <UserPlus size={24} className="text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">{pendingUsers.length}</p>
            <p className="text-sm text-slate-500">Pendientes</p>
          </div>
        </div>
        <div className="glass-panel p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
            <Shield size={24} className="text-violet-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">
              {users.filter((u) => u.role === "admin" || u.is_super_admin).length}
            </p>
            <p className="text-sm text-slate-500">Administradores</p>
          </div>
        </div>
      </div>

      {/* Tabs and Search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="glass-panel p-1 inline-flex rounded-full min-w-max">
            <button
              onClick={() => setActiveTab("all")}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all text-sm sm:text-base ${
                activeTab === "all"
                  ? "bg-primary text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="tab-all"
            >
              Todos ({users.length})
            </button>
            <button
              onClick={() => setActiveTab("pending")}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all flex items-center gap-2 text-sm sm:text-base ${
                activeTab === "pending"
                  ? "bg-primary text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="tab-pending"
            >
              Pendientes
              {pendingUsers.length > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === "pending" ? "bg-white/20" : "bg-amber-100 text-amber-700"
                }`}>
                  {pendingUsers.length}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Buscar usuarios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full glass-input pl-10"
            data-testid="search-users-input"
          />
        </div>
      </div>

      {/* Pending Users Alert */}
      {pendingUsers.length > 0 && activeTab === "all" && (
        <div className="glass-panel p-4 bg-amber-50 border-amber-200 flex items-center gap-4">
          <AlertCircle size={24} className="text-amber-600" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">
              Hay {pendingUsers.length} usuario(s) esperando activación
            </p>
            <p className="text-sm text-amber-600">
              Revisa la pestaña &quot;Pendientes&quot; para aprobar o rechazar sus solicitudes.
            </p>
          </div>
          <button
            onClick={() => setActiveTab("pending")}
            className="btn-primary bg-amber-500 hover:bg-amber-600"
          >
            Ver pendientes
          </button>
        </div>
      )}

      {/* Users List - Desktop Table */}
      <div className="glass-panel overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-4 font-semibold text-slate-700">Usuario</th>
                <th className="text-left p-4 font-semibold text-slate-700">Rol</th>
                <th className="text-left p-4 font-semibold text-slate-700">Estado</th>
                <th className="text-left p-4 font-semibold text-slate-700">Acceso</th>
                <th className="text-left p-4 font-semibold text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayUsers.map((u) => (
                <tr
                  key={u.user_id}
                  className="hover:bg-slate-50/50 transition-colors"
                  data-testid={`user-row-${u.user_id}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img
                          src={u.picture}
                          alt={u.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                          <span className="text-white font-medium">{u.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800">{u.name}</p>
                          {u.is_super_admin && (
                            <Shield size={14} className="text-primary" />
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`badge ${
                      u.role === "admin" ? "badge-primary" :
                      u.role === "director" ? "badge-success" :
                      u.role === "pending" ? "badge-warning" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      {roles.find((r) => r.id === u.role)?.name || u.role}
                    </span>
                  </td>
                  <td className="p-4">
                    {u.is_active ? (
                      <span className="badge badge-success flex items-center gap-1 w-fit">
                        <Check size={14} />
                        Activo
                      </span>
                    ) : (
                      <span className="badge badge-warning flex items-center gap-1 w-fit">
                        <AlertCircle size={14} />
                        Pendiente
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {u.assigned_directions?.length > 0 && (
                        <span className="badge bg-primary/10 text-primary text-xs">
                          {u.assigned_directions.length} direcciones
                        </span>
                      )}
                      {u.assigned_divisions?.length > 0 && (
                        <span className="badge bg-secondary/20 text-secondary-700 text-xs">
                          {u.assigned_divisions.length} divisiones
                        </span>
                      )}
                      {!u.assigned_directions?.length && !u.assigned_divisions?.length && (
                        <span className="text-sm text-slate-400">Sin asignar</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {!u.is_super_admin && (
                        <>
                          <button
                            onClick={() => setEmailModalUser(u)}
                            className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                            title="Enviar correo"
                          >
                            <Mail size={18} />
                          </button>
                          <button
                            onClick={() => { setCalendarModalUser(u); fetchCalendarEvents(); }}
                            className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors"
                            title="Agendar en calendario"
                          >
                            <Calendar size={18} />
                          </button>
                          <button
                            onClick={() => setSelectedUser(u)}
                            className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                            title="Editar permisos"
                            data-testid={`edit-user-${u.user_id}`}
                          >
                            <Edit size={18} />
                          </button>
                          {u.is_active ? (
                            <button
                              onClick={() => handleDeactivateUser(u.user_id)}
                              className="p-2 hover:bg-amber-50 text-amber-600 rounded-lg transition-colors"
                              title="Desactivar"
                              data-testid={`deactivate-user-${u.user_id}`}
                            >
                              <UserX size={18} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivateUser(u.user_id)}
                              className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors"
                              title="Activar"
                              data-testid={`activate-user-${u.user_id}`}
                            >
                              <UserCheck size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(u.user_id, u.name)}
                            className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                            title="Eliminar usuario"
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                      {u.is_super_admin && (
                        <span className="text-xs text-slate-400">Super Admin</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {displayUsers.length === 0 && (
          <div className="p-12 text-center">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">
              {activeTab === "pending"
                ? "No hay usuarios pendientes de activación"
                : "No se encontraron usuarios"}
            </p>
          </div>
        )}
      </div>

      {/* Users List - Mobile Cards */}
      <div className="md:hidden space-y-3">
        {displayUsers.map((u) => (
          <div
            key={u.user_id}
            className="glass-panel p-4"
            data-testid={`user-card-${u.user_id}`}
          >
            <div className="flex items-start gap-3 mb-3">
              {u.picture ? (
                <img
                  src={u.picture}
                  alt={u.name}
                  className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-medium text-lg">{u.name?.charAt(0)}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-800 truncate">{u.name}</p>
                  {u.is_super_admin && (
                    <Shield size={14} className="text-primary flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-slate-500 truncate">{u.email}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`badge ${
                u.role === "admin" ? "badge-primary" :
                u.role === "director" ? "badge-success" :
                u.role === "pending" ? "badge-warning" :
                "bg-slate-100 text-slate-600"
              }`}>
                {roles.find((r) => r.id === u.role)?.name || u.role}
              </span>
              {u.is_active ? (
                <span className="badge badge-success flex items-center gap-1">
                  <Check size={12} />
                  Activo
                </span>
              ) : (
                <span className="badge badge-warning flex items-center gap-1">
                  <AlertCircle size={12} />
                  Pendiente
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-1 mb-3">
              {u.assigned_directions?.length > 0 && (
                <span className="badge bg-primary/10 text-primary text-xs">
                  {u.assigned_directions.length} direcciones
                </span>
              )}
              {u.assigned_divisions?.length > 0 && (
                <span className="badge bg-secondary/20 text-secondary-700 text-xs">
                  {u.assigned_divisions.length} divisiones
                </span>
              )}
              {!u.assigned_directions?.length && !u.assigned_divisions?.length && (
                <span className="text-sm text-slate-400">Sin acceso asignado</span>
              )}
            </div>

            {!u.is_super_admin && (
              <div className="flex items-center gap-2 pt-3 border-t border-slate-100 flex-wrap">
                <button
                  onClick={() => setEmailModalUser(u)}
                  className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg"
                  title="Enviar correo"
                >
                  <Mail size={18} />
                </button>
                <button
                  onClick={() => { setCalendarModalUser(u); fetchCalendarEvents(); }}
                  className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-lg"
                  title="Agendar en calendario"
                >
                  <Calendar size={18} />
                </button>
                <button
                  onClick={() => setSelectedUser(u)}
                  className="flex-1 btn-secondary py-2 text-sm flex items-center justify-center gap-2 min-w-0"
                  data-testid={`edit-user-mobile-${u.user_id}`}
                >
                  <Edit size={16} />
                  Editar
                </button>
                {u.is_active ? (
                  <button
                    onClick={() => handleDeactivateUser(u.user_id)}
                    className="p-2 hover:bg-amber-50 text-amber-600 rounded-lg transition-colors"
                    title="Desactivar"
                  >
                    <UserX size={20} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleActivateUser(u.user_id)}
                    className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors"
                    title="Activar"
                  >
                    <UserCheck size={20} />
                  </button>
                )}
                <button
                  onClick={() => handleDeleteUser(u.user_id, u.name)}
                  className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                  title="Eliminar"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            )}
            {u.is_super_admin && (
              <div className="pt-3 border-t border-slate-100 text-center">
                <span className="text-xs text-slate-400">Super Admin - Sin acciones disponibles</span>
              </div>
            )}
          </div>
        ))}

        {displayUsers.length === 0 && (
          <div className="glass-panel p-12 text-center">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">
              {activeTab === "pending"
                ? "No hay usuarios pendientes de activación"
                : "No se encontraron usuarios"}
            </p>
          </div>
        )}
      </div>

        </>
      )}

      {/* Edit Permissions Modal - portal a body para overlay completo */}
      {selectedUser && createPortal(
        <EditPermissionsModal
          user={selectedUser}
          modules={modules}
          roles={roles}
          onClose={() => setSelectedUser(null)}
          onSave={handleUpdatePermissions}
        />,
        document.body
      )}

      {/* Send Email Modal */}
      {emailModalUser && createPortal(
        <SendEmailModal
          user={emailModalUser}
          onClose={() => setEmailModalUser(null)}
          onSendNow={async (payload) => {
            await axios.post(`${API}/admin/send-email`, payload, {
              withCredentials: true,
              timeout: 20000,
            });
            toast.success("Correo enviado correctamente");
          }}
        />,
        document.body
      )}

      {/* Add event to one user's calendar */}
      {calendarModalUser && createPortal(
        <AddToCalendarModal
          user={calendarModalUser}
          events={calendarEvents}
          onClose={() => setCalendarModalUser(null)}
          onAdd={(eventId) => handleAddEventToUserCalendar(calendarModalUser.user_id, eventId)}
        />,
        document.body
      )}

      {/* Add event to all users' calendars */}
      {addToAllCalendarOpen && createPortal(
        <AddToAllCalendarsModal
          events={calendarEvents}
          onClose={() => setAddToAllCalendarOpen(false)}
          onAdd={handleAddEventToAllCalendars}
        />,
        document.body
      )}

      <ConfirmModal
        open={confirmModal.open}
        onClose={() => setConfirmModal((c) => ({ ...c, open: false }))}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
      />
    </div>
  );
};

// Modal: Enviar correo (mailto o envío desde el servidor si SMTP está configurado)
const SendEmailModal = ({ user, onClose, onSendNow }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const email = user?.email || "";
  const handleOpenMail = () => {
    const url = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    onClose();
  };
  const handleSendNow = async () => {
    if (!subject.trim()) {
      setError("Escribe un asunto");
      return;
    }
    setError("");
    setSending(true);
    try {
      await onSendNow({ to_user_ids: [user.user_id], subject: subject.trim(), body: body.trim() });
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = detail
        ? (typeof detail === "string" ? detail : JSON.stringify(detail))
        : e.code === "ECONNABORTED"
          ? "Tiempo de espera agotado. Comprueba que SMTP esté configurado en el servidor."
          : e.message || "Error al enviar. Revisa la consola.";
      setError(msg);
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="modal-overlay fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-4 py-6">
      <div className="glass-panel p-6 w-full max-w-md my-auto animate-slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Mail size={20} />
            Enviar correo
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-slate-600 mb-2">Para: <span className="font-medium">{email}</span></p>
        <input
          type="text"
          placeholder="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full glass-input mb-3"
        />
        <textarea
          placeholder="Mensaje"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className="w-full glass-input mb-4 resize-none"
        />
        {error && <p className="text-sm text-rose-600 mb-2">{error}</p>}
        <div className="flex flex-wrap gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={handleOpenMail} className="btn-secondary flex items-center gap-2">
            <Mail size={16} />
            Abrir en correo
          </button>
          {onSendNow && (
            <button
              onClick={handleSendNow}
              disabled={sending}
              className="btn-primary flex items-center gap-2"
            >
              {sending ? "Enviando…" : "Enviar ahora"}
              <Mail size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal: Añadir evento al calendario de un usuario
const AddToCalendarModal = ({ user, events, onClose, onAdd }) => {
  const [eventId, setEventId] = useState("");
  const [adding, setAdding] = useState(false);
  const handleAdd = async () => {
    if (!eventId) return;
    setAdding(true);
    try {
      await onAdd(eventId);
      onClose();
    } finally {
      setAdding(false);
    }
  };
  return (
    <div className="modal-overlay fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-4 py-6">
      <div className="glass-panel p-6 w-full max-w-md my-auto animate-slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Calendar size={20} />
            Agendar en calendario
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-slate-600 mb-3">Añadir evento al calendario de <span className="font-medium">{user?.name}</span></p>
        <select
          value={eventId}
          onChange={(e) => setEventId(e.target.value)}
          className="w-full glass-input mb-4"
        >
          <option value="">Seleccionar evento</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.title} ({ev.date})</option>
          ))}
        </select>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={handleAdd} disabled={!eventId || adding} className="btn-primary flex items-center gap-2">
            {adding ? "Añadiendo…" : "Añadir al calendario"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal: Añadir evento a todos los calendarios
const AddToAllCalendarsModal = ({ events, onClose, onAdd }) => {
  const [eventId, setEventId] = useState("");
  const [adding, setAdding] = useState(false);
  const handleAdd = async () => {
    if (!eventId) return;
    setAdding(true);
    try {
      await onAdd(eventId);
      onClose();
    } finally {
      setAdding(false);
    }
  };
  return (
    <div className="modal-overlay fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-4 py-6">
      <div className="glass-panel p-6 w-full max-w-md my-auto animate-slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Calendar size={20} />
            Añadir evento a todos los calendarios
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-slate-600 mb-3">Se añadirá el evento al Google Calendar de cada usuario que lo tenga vinculado.</p>
        <select
          value={eventId}
          onChange={(e) => setEventId(e.target.value)}
          className="w-full glass-input mb-4"
        >
          <option value="">Seleccionar evento</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.title} ({ev.date})</option>
          ))}
        </select>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={handleAdd} disabled={!eventId || adding} className="btn-primary flex items-center gap-2">
            {adding ? "Añadiendo…" : "Añadir a todos"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Permissions Modal Component
const EditPermissionsModal = ({ user, modules, roles, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    role: user.role || "member",
    is_active: user.is_active || false,
    assigned_directions: user.assigned_directions || [],
    assigned_divisions: user.assigned_divisions || [],
  });

  const toggleDirection = (dirId) => {
    setFormData((prev) => ({
      ...prev,
      assigned_directions: prev.assigned_directions.includes(dirId)
        ? prev.assigned_directions.filter((d) => d !== dirId)
        : [...prev.assigned_directions, dirId],
    }));
  };

  const toggleDivision = (divId) => {
    setFormData((prev) => ({
      ...prev,
      assigned_divisions: prev.assigned_divisions.includes(divId)
        ? prev.assigned_divisions.filter((d) => d !== divId)
        : [...prev.assigned_divisions, divId],
    }));
  };

  const selectAllDirections = () => {
    setFormData((prev) => ({
      ...prev,
      assigned_directions: modules.directions.map((d) => d.id),
    }));
  };

  const selectAllDivisions = () => {
    setFormData((prev) => ({
      ...prev,
      assigned_divisions: modules.divisions.map((d) => d.id),
    }));
  };

  const handleSubmit = () => {
    onSave(user.user_id, formData);
  };

  return (
    <div className="modal-overlay fixed inset-0 min-h-[100dvh] h-[100dvh] bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-4 py-6">
      <div className="glass-panel p-8 w-full max-w-2xl max-h-[calc(100dvh-3rem)] overflow-y-auto my-auto animate-slide-up recursos-scrollbar-hide" data-testid="edit-permissions-modal">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-14 h-14 rounded-full object-cover"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-white font-bold text-xl">{user.name?.charAt(0)}</span>
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-slate-800">{user.name}</h2>
              <p className="text-slate-500">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">Rol</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {roles.filter(r => r.id !== "pending").map((role) => (
                <button
                  key={role.id}
                  onClick={() => setFormData({ ...formData, role: role.id })}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    formData.role === role.id
                      ? "border-primary bg-primary/5"
                      : "border-slate-200 hover:border-primary/50"
                  }`}
                >
                  <p className="font-medium text-slate-800">{role.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{role.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
            <div>
              <p className="font-medium text-slate-800">Estado de la cuenta</p>
              <p className="text-sm text-slate-500">
                Los usuarios inactivos no pueden acceder al sistema
              </p>
            </div>
            <button
              onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
              className={`w-14 h-8 rounded-full transition-colors relative ${
                formData.is_active ? "bg-emerald-500" : "bg-slate-300"
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full bg-white shadow-md absolute top-1 transition-all ${
                  formData.is_active ? "right-1" : "left-1"
                }`}
              ></div>
            </button>
          </div>

          {/* Directions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700">Direcciones</label>
              <button
                onClick={selectAllDirections}
                className="text-xs text-primary hover:underline"
              >
                Seleccionar todas
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {modules.directions.map((dir) => (
                <button
                  key={dir.id}
                  onClick={() => toggleDirection(dir.id)}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    formData.assigned_directions.includes(dir.id)
                      ? "border-primary bg-primary/5"
                      : "border-slate-200 hover:border-primary/50"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-md flex items-center justify-center ${
                      formData.assigned_directions.includes(dir.id)
                        ? "bg-primary text-white"
                        : "border-2 border-slate-300"
                    }`}
                  >
                    {formData.assigned_directions.includes(dir.id) && (
                      <Check size={14} />
                    )}
                  </div>
                  <span className="text-sm text-slate-700">{dir.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Divisions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700">Divisiones</label>
              <button
                onClick={selectAllDivisions}
                className="text-xs text-primary hover:underline"
              >
                Seleccionar todas
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {modules.divisions.map((div) => (
                <button
                  key={div.id}
                  onClick={() => toggleDivision(div.id)}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    formData.assigned_divisions.includes(div.id)
                      ? "border-secondary bg-secondary/10"
                      : "border-slate-200 hover:border-secondary/50"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-md flex items-center justify-center ${
                      formData.assigned_divisions.includes(div.id)
                        ? "bg-secondary text-slate-800"
                        : "border-2 border-slate-300"
                    }`}
                  >
                    {formData.assigned_divisions.includes(div.id) && (
                      <Check size={14} />
                    )}
                  </div>
                  <span className="text-sm text-slate-700">{div.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button onClick={onClose} className="btn-secondary">
              Cancelar
            </button>
            <button onClick={handleSubmit} className="btn-primary flex items-center gap-2" data-testid="save-permissions-btn">
              <Save size={18} />
              Guardar cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
